import {
  ErrorBoundary,
  Facet,
  Paging,
  PagingInfo,
  Results,
  SearchProvider,
  WithSearch,
} from '@elastic/react-search-ui';
import {
  Layout,
  MultiCheckboxFacet,
  PagingInfoViewProps,
} from '@elastic/react-search-ui-views';
import { SearchResult } from '@elastic/search-ui';
import React, { useEffect, useRef } from 'react';
import { useSearchParams } from 'next/navigation';

import { isBrowser } from '../../helpers/is-browser';
import ImageWrapper from '../image-wrapper/Image-wrapper';
import { searchDriver } from './search-driver';
import Link from 'next/link';
import {
  useIsMPLivliFamily,
  useTenantConfig,
} from '@/providers/ConfigProvider';
import { useUser } from '@/providers/UserProvider';

const BACK_NAV_FLAG = 'search-back-nav';

const SCROLL_POS_KEY = 'search-scroll-pos';

const ScrollRestorer = ({
  isLoading,
  wasSearched,
  currentQuery,
}: {
  isLoading: boolean | undefined;
  wasSearched: boolean | undefined;
  currentQuery: string;
}) => {
  const restoreOnLoadRef = useRef(false);

  const finishRestore = (targetY: number) => {
    window.scrollTo(0, targetY);
    sessionStorage.setItem(SCROLL_POS_KEY, String(targetY));
    restoreOnLoadRef.current = false;
  };

  useEffect(() => {
    if (!isBrowser) return;
    history.scrollRestoration = 'manual';
    if (sessionStorage.getItem(BACK_NAV_FLAG) === currentQuery) {
      restoreOnLoadRef.current = true;
    } else {
      sessionStorage.removeItem(BACK_NAV_FLAG);
      sessionStorage.removeItem(SCROLL_POS_KEY);
      window.scrollTo(0, 0);
    }
  }, [currentQuery]);

  // Always keeps the saved position current.
  useEffect(() => {
    if (!isBrowser) return;

    const save = () => {
      if (restoreOnLoadRef.current) return;

      const activeQuery = new URLSearchParams(window.location.search).get('q') ?? '';
      if (activeQuery !== currentQuery) return;

      sessionStorage.setItem(SCROLL_POS_KEY, String(window.scrollY));
    };

    window.addEventListener('scroll', save, { passive: true });
    return () => window.removeEventListener('scroll', save);
  }, [currentQuery]);

  // Back/forward while the component stays mounted (same query, router cache hit).
  useEffect(() => {
    if (!isBrowser) return;
    let rafId: number;

    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search);
      const targetQuery = params.get('q') ?? '';
      if (sessionStorage.getItem(BACK_NAV_FLAG) !== targetQuery) return;

      const saved = sessionStorage.getItem(SCROLL_POS_KEY);
      if (!saved) return;

      const targetY = parseInt(saved, 10);
      restoreOnLoadRef.current = true;
      cancelAnimationFrame(rafId);
      const attempt = () => {
        if (document.documentElement.scrollHeight >= targetY + window.innerHeight) {
          finishRestore(targetY);
        } else {
          rafId = requestAnimationFrame(attempt);
        }
      };
      rafId = requestAnimationFrame(attempt);
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
      cancelAnimationFrame(rafId);
    };
  }, []);

  // Handle isLoading changes:
  //   isLoading = true  → new search starting   → scroll to top (unless back-nav remount)
  //   isLoading = false → search finished        → restore scroll (if back-nav remount)
  useEffect(() => {
    if (!isBrowser) return;

    if (isLoading) {
      if (!restoreOnLoadRef.current) {
        // User triggered a new search — reset everything.
        window.scrollTo(0, 0);
        sessionStorage.removeItem(BACK_NAV_FLAG);
        sessionStorage.removeItem(SCROLL_POS_KEY);
      }
      return;
    }

    // isLoading is false and we have results — restore for back-nav remount.
    if (wasSearched && restoreOnLoadRef.current) {
      const saved = sessionStorage.getItem(SCROLL_POS_KEY);
      if (!saved) return;
      const targetY = parseInt(saved, 10);
      let rafId: number;
      const attempt = () => {
        if (document.documentElement.scrollHeight >= targetY + window.innerHeight) {
          finishRestore(targetY);
          // Do NOT remove the flag — kept alive for subsequent back/forward cycles.
        } else {
          rafId = requestAnimationFrame(attempt);
        }
      };
      rafId = requestAnimationFrame(attempt);
      return () => cancelAnimationFrame(rafId);
    }
  }, [isLoading, wasSearched]);

  return null;
};

//called on paging and facet so that position is set to top and scroll position doesn't go to footer
const scrollToTopOnClick = () => window.scrollTo(0, 0);

interface SearchResultsProps {
  title: string;
  noHitsTitle: string;
  noHitsText: string;
  noHitsImage?: any;
  node_locale: string;
}

export const SearchResults = ({
  title,
  noHitsTitle,
  noHitsText,
  noHitsImage,
  node_locale,
}: SearchResultsProps) => {
  const isLivliFamily = useIsMPLivliFamily();
  const { user } = useUser();
  const config = useTenantConfig();
  const elasticEngineName =
    config?.Elastic_Engine || config?.NEXT_PUBLIC_ELASTIC_ENGINE;
  const searchParams = useSearchParams();
  const searchQuery = searchParams.get('q') || '';

  const PageHeaderView = ({ searchTerm }: PagingInfoViewProps) => (
    <h1 className="mb-10 mt-2 font-title text-headingm text-text-default">
      {title.replace('{0}', `"${searchTerm}"`)}
    </h1>
  );

  const NoResults = () => (
    <div className="mx-auto max-w-xs text-center text-text-default">
      {noHitsImage && (
        <ImageWrapper alt="Logotype" url={noHitsImage.url} className="mb-8" />
      )}
      {noHitsTitle && (
        <h1 className="font-title text-headingm">{noHitsTitle}</h1>
      )}
      {noHitsText && <p className="mt-4 text-bodym">{noHitsText}</p>}
    </div>
  );

  const ResultsView = ({ result }: { result: SearchResult }) => (
    <li className="mb-2">
      <Link
        href={result.url_path.raw}
        onClick={() => {
          if (isBrowser) {
            // Set the flag (= current query) and snapshot position.
            sessionStorage.setItem(BACK_NAV_FLAG, searchQuery);
            sessionStorage.setItem(SCROLL_POS_KEY, String(window.scrollY));
          }
        }}
        className="rounded-xl bg-background-primary-light p-5 leading-none text-text-default  flex lg:hover:bg-background-secondary justify-between items-center"
      >
        <div>
          <h2 className="font-title">{result.search_title?.raw}</h2>
          <span className="mt-0.5 block text-bodys">
            {result.page_category?.raw}
          </span>
          {result?.tag?.raw && (
            <span className="inline-block border-2 py-1 px-2 mt-2 rounded-[4px] text-headingxxs text-neutral-grey-700 border-brand-yellow-2">
              {result?.tag?.raw}
            </span>
          )}
        </div>
        {result.page_image?.raw && (
          <div className="h-12 w-12 lg:h-10 lg:w-10 rounded-[4px] flex justify-center bg-brand-white items-center ml-6 border border-brand-grey-2">
            <img
              src={result.page_image.raw}
              alt={result.page_image.alt || 'alt text not found'}
              className="h-10 w-10 object-contain rounded-[4px]"
            />
          </div>
        )}
      </Link>
    </li>
  );

  return (
    <div>
      <SearchProvider
        key={searchQuery}
        config={searchDriver(node_locale, {
          elasticEngineName: elasticEngineName,
        })}
      >
        <WithSearch
          mapContextToProps={({
            wasSearched,
            totalResults,
            isLoading,
            searchTerm,
          }) => ({
            wasSearched,
            totalResults,
            isLoading,
            searchTerm,
          })}
        >
          {({ wasSearched, totalResults, isLoading, searchTerm }) => {
            // Push GTM event on search
            wasSearched &&
              isBrowser &&
              searchTerm &&
              dataLayer.push({
                event: 'site_search',
                search_term: searchTerm,
                search_category: 'No search Filter. Global search used',
                total_results: totalResults ?? 0,
                user_status: isLivliFamily
                  ? user
                    ? 'logged_in'
                    : 'guest'
                  : undefined,
              });

            return (
              <>
                <ScrollRestorer
                  isLoading={isLoading}
                  wasSearched={wasSearched}
                  currentQuery={searchQuery}
                />
                <ErrorBoundary>
                  <Layout
                    bodyContent={
                      <>
                        {(totalResults ?? 0) > 0 && (
                          <Results
                            titleField="search_title"
                            urlField="url_path"
                            thumbnailField="page_image"
                            resultView={ResultsView}
                          />
                        )}
                        {!isLoading && (totalResults ?? 0) === 0 && (
                          <NoResults />
                        )}
                      </>
                    }
                    bodyHeader={
                      <>
                        {wasSearched && <PagingInfo view={PageHeaderView} />}
                        {wasSearched && (
                          <div
                            className="-mt-3 mb-5 overflow-x-auto pt-3 no-scrollbar max-lg:-mx-6 max-lg:px-6 lg:-ml-3 lg:pl-3"
                            onClick={scrollToTopOnClick}
                          >
                            <Facet
                              field="page_category"
                              label="Category"
                              isFilterable={false}
                              show={20}
                              filterType="any"
                              view={(props) => (
                                <MultiCheckboxFacet {...props} />
                              )}
                            />
                          </div>
                        )}
                      </>
                    }
                    bodyFooter={
                      <div onClick={scrollToTopOnClick}>
                        <Paging />
                      </div>
                    }
                  />
                </ErrorBoundary>
              </>
            );
          }}
        </WithSearch>
      </SearchProvider>
    </div>
  );
};
